<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12 mb-3">
        <Button color="secondary" variant="outline" class="btn-sm mb-0" @click="goBack">
          <i class="fa-solid fa-arrow-left me-1" aria-hidden="true"></i>{{ $t('key_log_back_to_list') }}
        </Button>
      </div>
      <div class="col-12">
        <div class="card">
          <ImportLogListTableHeader
            :title="$t('key_importLogList_table_title')"
            :description="$t('key_importLogList_table_description')"
            :enable-reload="true"
            :enable-search="true"
            :is-exporting="isExporting"
            :list-filters="filters"
            @search-change="searchChange"
            @reload-page="reloadPage"
            @log-type-filter-change="logTypeFilterChange"
            @clear-all-filters="clearAllFilters"
            @export-logs="onExportLogs">
          </ImportLogListTableHeader>

          <GenericTableV2
            ref="genericTable"
            :items="logItems"
            :pagination="pagination"
            :headers="headers"
            :is-loading="isLoading"
            @changed-header="onChangeHeader"
            @changed-page="paginationPageChange"
            @changed-row="paginationRowChange">
            <template #row="{item}">
              <td v-for="column in columns" :key="column.key" class="text-xs font-weight-bolder">
                <span v-if="column.type === 'logType'" class="badge badge-sm" :class="logTypeBadgeClass(resolveValue(item, column.path))">
                  {{ resolveValue(item, column.path) }}
                </span>
                <span v-else-if="column.type === 'json'">
                  {{ formatMetadata(resolveValue(item, column.path)) }}
                </span>
                <span v-else>
                  {{ formatCell(resolveValue(item, column.path)) }}
                </span>
              </td>
            </template>
          </GenericTableV2>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

  import { mapState, mapActions } from "vuex";
  import Button from "./../../../../generic_components/Button.vue";
  import GenericTableV2 from "./../../../../generic_components/GenericTableV2.vue";
  import ImportLogListTableHeader from "./../components/ImportLogListTableHeader.vue";
  import { getLogColumns, resolveLogValue } from "./../columns/logColumns.js";

  export default {
    name: "ImportLogList",
    components: {
      Button,
      GenericTableV2,
      ImportLogListTableHeader,
    },
    data() {
      return {
        myAccount: JSON.parse(localStorage.getItem("user")),
        processId: this.$route.params.id,
        entityType: this.$route.query.entity_type || '',
        filters: {
          search: '',
          logType: '',
          sort: {
            sort: '',
            order: 'asc'
          }
        },
        isLoading: false,
        isExporting: false,
        pagination: {
          total: 0,
          rows: 10,
          currentPage: 1,
          pages: {
            total: 10,
            current: 1
          },
          entries: [5, 10, 15, 20, 50, 100, 200]
        }
      }
    },
    computed: {
      ...mapState('import', {
        logsData: state => state.logsList.data,
        logsResponse: state => state.logsList.response,
        logsStatus: state => state.logsList.status,
        exportData: state => state.logsExport.data,
        exportResponse: state => state.logsExport.response,
        exportStatus: state => state.logsExport.status,
      }),
      columns() {
        return getLogColumns(this.entityType);
      },
      logItems() {
        // GenericTableV2 keys rows on `_id`; log entries only carry `id`.
        return (this.logsData?.items || []).map(item => ({ ...item, _id: item.id }));
      },
      headers() {
        return this.columns.map(column => ({
          title: this.$t(column.titleKey),
          sort: { sorteable: !!column.sortField, order: "" },
          column: column.sortField || column.key
        }));
      }
    },
    watch: {
      logsResponse() {
        if (this.logsData) {
          this.pagination.total = this.logsData.total;
          this.isLoading = false;
        }
      }
    },
    mounted() {
      if (this.$route.query.log_type) {
        this.filters.logType = this.$route.query.log_type;
      }
      this.getLogsList();
    },
    methods: {
      ...mapActions('import', [
                                'getImportLogs',
                                'exportImportLogs'
                              ]),
      resolveValue(item, path) {
        return resolveLogValue(item, path);
      },
      formatCell(value) {
        return value === null || value === undefined || value === '' ? '-' : value;
      },
      formatMetadata(value) {
        if (value === null || value === undefined) {
          return '-';
        }
        return JSON.stringify(value);
      },
      logTypeBadgeClass(logType) {
        if (logType === 'OK') {
          return 'badge-imported';
        }
        if (logType === 'WARNING') {
          return 'badge-warning';
        }
        return 'badge-error';
      },
      goBack() {
        this.$router.push({ path: '/Connections/ImportedFiles' });
      },
      async getLogsList() {
        if (this.logsStatus === 'loading' || this.isLoading) {
          return;
        }

        this.isLoading = true;
        try {
          await this.getImportLogs({ processId: this.processId, filters: this.filters, pagination: this.pagination });

          if (this.logsStatus !== 'success') {
            this.$notify({
              title: this.$t('key_header_error_retrieving_data'),
              message: this.$t('key_message_error_retrieving_data'),
              icon: { component: 'fa-solid fa-xmark', color: 'white' },
              color: 'danger',
              show: true,
              duration: 5000
            });
          }
        } catch (error) {
          this.$notify({
            title: this.$t('key_header_error_unexpected'),
            message: this.$t('key_message_error_unexpected'),
            icon: { component: 'fa-solid fa-xmark', color: 'white' },
            color: 'danger',
            show: true,
            duration: 5000
          });
        } finally {
          this.isLoading = false;
        }
      },
      async clearAllFilters() {
        this.filters.search = '';
        this.filters.logType = '';
        this.pagination.currentPage = 1;
        this.$notify({
          title: this.$t('key_header_filters_cleared'),
          message: this.$t('key_message_filters_cleared'),
          icon: { component: 'fa-solid fa-eraser', color: 'white' },
          color: 'info',
          show: true,
          duration: 2500
        });
        await this.getLogsList();
      },
      async searchChange(value) {
        this.pagination.currentPage = 1;
        this.filters.search = value;
        await this.getLogsList();
      },
      async reloadPage() {
        await this.getLogsList();
      },
      async logTypeFilterChange(value) {
        this.pagination.currentPage = 1;
        this.filters.logType = value;
        await this.getLogsList();
      },
      async onChangeHeader(val) {
        this.filters.sort = {
          sort: val.column,
          order: val.order == "asc" ? "ascending" : "descending"
        };
        await this.getLogsList();
      },
      async paginationPageChange(page) {
        this.pagination.currentPage = page;
        await this.getLogsList();
      },
      async paginationRowChange(rows) {
        this.pagination.rows = rows;
        await this.getLogsList();
      },
      async onExportLogs() {
        if (this.exportStatus === 'waiting' || this.isExporting) {
          return;
        }

        this.isExporting = true;
        try {
          await this.exportImportLogs({ processId: this.processId, language: this.$i18n.locale });

          if (this.exportStatus === 'success' && this.exportResponse.status === 200) {
            this.downloadExcel(this.exportData, `import_logs_${this.processId}.xlsx`);
            this.$notify({
              title: this.$t('key_header_successful_downloading_file'),
              message: this.$t('key_message_successful_downloading_file'),
              icon: { component: 'fa-solid fa-download', color: 'white' },
              color: 'success',
              show: true,
              duration: 5000
            });
          } else {
            this.$notify({
              title: this.$t('key_header_error_downloading_file'),
              message: this.$t('key_message_error_downloading_file'),
              icon: { component: 'fa-solid fa-bomb', color: 'white' },
              color: 'danger',
              show: true,
              duration: 5000
            });
          }
        } catch (error) {
          this.$notify({
            title: this.$t('key_header_error_unexpected'),
            message: this.$t('key_message_error_unexpected'),
            icon: { component: 'fa-solid fa-xmark', color: 'white' },
            color: 'danger',
            show: true,
            duration: 5000
          });
        } finally {
          this.isExporting = false;
        }
      },
      downloadExcel(blob, filename) {
        const link = document.createElement("a");
        link.setAttribute('download', filename);
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
      },
    }
  };

</script>
